import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { errMessage } from "@/lib/api";
import { usePageMeta } from "@/lib/format";
import { useT } from "@/i18n";
import { AuthShell } from "@/components/auth/AuthShell";
import { SocialButtons } from "@/components/auth/SocialButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";

export default function Signup() {
  const t = useT();
  usePageMeta(t("signup.meta_title"), t("signup.meta_desc"));
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const roles = [
    { value: "client", label: t("signup.role_client") },
    { value: "coach", label: t("signup.role_coach") },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("signup.consent_required"));
      return;
    }
    setLoading(true);
    try {
      await signup({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        consent: {
          terms: true,
          article_9: true,
          age_confirmed: true,
          consent_version: "web-2026-06",
        },
      });
      toast.success(t("signup.created"));
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(errMessage(err, t("signup.failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("signup.title")}
      subtitle={t("signup.subtitle")}
      footer={<>{t("signup.footer_q")} <Link to="/login" className="font-medium text-primary hover:underline">{t("signup.footer_link")}</Link></>}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
        <div className="grid grid-cols-2 gap-2">
          {roles.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, role: r.value }))}
              data-testid={`role-${r.value}`}
              className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
                form.role === r.value
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="name">{t("signup.name")}</Label>
          <Input id="name" required value={form.name} onChange={set("name")} placeholder={t("signup.name_placeholder")} data-testid="signup-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} placeholder={t("auth.email_placeholder")} data-testid="signup-email" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={set("password")} placeholder={t("signup.password_placeholder")} data-testid="signup-password" />
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-secondary/40 p-3">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="signup-consent" />
          <span className="text-xs leading-relaxed text-muted-foreground">
            {t("signup.consent_pre")} <a href="https://my-within.com/terms" className="text-primary hover:underline">{t("signup.terms")}</a> {t("signup.consent_mid")}{" "}
            <a href="https://my-within.com/privacy" className="text-primary hover:underline">{t("signup.privacy")}</a>{t("signup.consent_post")}
          </span>
        </label>

        <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading} data-testid="signup-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("signup.submit")}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("common.or")} <div className="h-px flex-1 bg-border" />
      </div>
      <SocialButtons />
    </AuthShell>
  );
}
